import { Browser } from "../core/browser";

export class Table {
	constructor(table) {
		this.Table = table;
		this.Caption = table.parentElement?.nextElementSibling;
		this.SelectedCountLabel = this.Caption?.querySelector(".selected-count");
		this.TableHead = table.querySelector("thead");
		this.ForbidMultiSelect = !table.dataset.multiselect;

		this.DelegateEvents();

		this.RowSelector = "tbody tr:not(.d-none)";
		this.DataRowSelector = "tbody tr:not(.d-none, .table-body-label)";
		this.ValidRowSelector = "tbody tr:not(.table-body-label)";
		this.HeaderSelector = "thead th";

		this.LastSelectedIndex = -1;
	}

	#OnTableBodyClick(tBody, ev) {
		const row = this.GetRowFromEvent(ev, true);

		if (row) {
			if (!row.classList.contains("table-body-label")) {
				this.SelectRow(row, !ev.ctrlKey && !ev.shiftKey, ev.shiftKey);

				this.Table.dispatchEvent(new CustomEvent("rowclick", { detail: { row: row } }));
			} else if (tBody.classList.contains("collapsed")) {
				tBody.classList.remove("collapsed");
			} else {
				tBody.classList.add("collapsed");
			}
		}
	}

	#OnTableBodyMouseDown(ev) {
		const row = this.GetRowFromEvent(ev);

		if (row) {
			if (ev.button === 2 && !row.classList.contains("active")) {
				this.SelectRow(row, !ev.ctrlKey && !ev.shiftKey, ev.shiftKey);
			} else if (ev.button === 1 && !ev.target?.closest("a")) {
				if (!row.classList.contains("active")) {
					this.SelectRow(row, !ev.ctrlKey && !ev.shiftKey, ev.shiftKey);
				}
				ev.preventDefault(); // Prevent scrolling with middle mouse, can't be done in mouseup event
			}

			this.Table.dispatchEvent(new CustomEvent("rowmousedown", { detail: { row: row } }));
		}
	}

	#OnTableBodyMouseUp(ev) {
		const row = this.GetRowFromEvent(ev);

		if (row) {
			this.Table.dispatchEvent(new CustomEvent("rowmouseup", { detail: { row: row } }));

			if (ev.button === 1 && !ev.target?.closest("a")) {
				this.PickRow(row, "_blank");
				this.Table.dispatchEvent(new CustomEvent("rowauxclick", { detail: { row: row } }));
			}
		}
	}

	#OnTableBodyDblClick(ev) {
		const row = this.GetRowFromEvent(ev);

		if (row) {
			this.OpenRowURL(row, "_self");

			this.Table.dispatchEvent(new CustomEvent("rowdblclick", { detail: { row: row } }));
			this.PickRow(row);
		}
	}

	#OnTableBodyContextMenu(ev) {
		this.Table.dispatchEvent(new CustomEvent("rowcontextmenu", { detail: { row: ev.target.matches(this.DataRowSelector) ? ev.target : ev.target.closest(this.DataRowSelector) } }));
	}

	#OnTableBodyKeyUp(ev) {
		if (ev.keyCode === 13) {
			const row = this.GetRowFromEvent(ev);

			if (row) {
				this.PickRow(row);
			}
		}
	}

	#OnTableBodyKeyDown(ev) {
		switch (ev.keyCode) {
			case 40:
				// Down arrow
				ev.preventDefault();
				this.SelectNextRow(ev.shiftKey, !ev.shiftKey);
				break;
			case 38:
				// Up arrow
				ev.preventDefault();
				this.SelectPreviousRow(ev.shiftKey, !ev.shiftKey);
				break;
			case 65:
				// CTRL + A
				if (ev.ctrlKey && !this.ForbidMultiSelect) {
					ev.preventDefault();

					this.Table.querySelectorAll(this.DataRowSelector).forEach(row => row.classList.add("active"));
					this.UpdateSelectedCount();
				}
				break;
			case 67:
				// CTRL + C
				if (ev.ctrlKey) {
					ev.preventDefault();
					this.Table.dispatchEvent(new CustomEvent("rowscopied", { detail: { rows: this.GetSelectedRows() } }));
				}
				break;
		}
	}

	DelegateEvents() {
		if (this.Table.classList.contains("table-sortable")) {
			let timeout,
				sortOrder = Browser.GetURLParameter("o") ?? "";

			this.TableHead?.addEventListener("click", ev => {
				if (timeout) {
					clearTimeout(timeout);
				}

				let requestedField = ev.target.matches("th") ? ev.target : ev.target.closest("th"),
					requestedSort = this.GetNextSort(requestedField);

				let icon = requestedField.querySelector("chip-icon");

				if (!icon) {
					const appended = document.createElement("chip-icon");
					requestedField.firstElementChild.appendChild(appended);

					icon = appended;
				}

				if (icon.icon.includes("fa-chevron-down")) {
					icon.remove();
				} else if (icon.icon.includes("fa-chevron-up")) {
					icon.icon = "fas fa-sm fa-chevron-down ms-2";
				} else {
					icon.icon = "fas fa-sm fa-chevron-up ms-2";
				}

				timeout = setTimeout(() => {
					if (sortOrder !== requestedSort) {
						Browser.SetURLParameter("o", requestedSort.toLowerCase() || null);
					}
				}, 500);
			});
		}

		Array.from(this.Table.tBodies).forEach(tBody => {
			Object.assign(tBody, {
				onclick: ev => this.#OnTableBodyClick(tBody, ev),
				onmousedown: ev => this.#OnTableBodyMouseDown(ev),
				onmouseup: ev => this.#OnTableBodyMouseUp(ev),
				ondblclick: ev => this.#OnTableBodyDblClick(ev),
				oncontextmenu: ev => this.#OnTableBodyContextMenu(ev),
				onkeyup: ev => this.#OnTableBodyKeyUp(ev),
				onkeydown: ev => this.#OnTableBodyKeyDown(ev)
			});
		});
	}

	GetNextSort(header) {
		let sort = "";
		const icon = header.querySelector("chip-icon");

		if (!icon) {
			sort = `ASC${header.dataset.field}`;
		} else if (icon.icon.includes("fa-chevron-up")) {
			sort = `DESC${header.dataset.field}`;
		}

		return sort.toLowerCase();
	}

	SortHeader(header) { Browser.SetURLParameter("o", this.GetNextSort(header)) }

	SortByName(name) {
		const existing = Browser.GetURLParameter("o") ?? "";

		if (!existing.includes(name)) {
			Browser.SetURLParameter("o", (name.trim() !== "" ? `ASC${name}` : "").toLowerCase());
		} else if (existing.slice(0, 4).toLowerCase() === "desc") {
			return Browser.SetURLParameter("o", null);
		} else {
			return Browser.SetURLParameter("o", (name.trim() !== "" ? `DESC${name}` : "").toLowerCase());
		}
	}

	OpenRowURL(row, type) {
		if (row && this.Table.dataset.externalPointer) {
			window.open(this.Table.dataset.externalPointer.replace("{id}", row.dataset.identifier), type);
		}
	}

	PickRow(row, openType) {
		const rowsSelected = this.GetSelectedRows();

		if (rowsSelected.length && this.Table.dataset.externalPointer) {
			if (rowsSelected.length > 1) {
				rowsSelected.forEach(x => this.OpenRowURL(x, "_blank"));
			} else {
				this.OpenRowURL(rowsSelected[0], openType ?? "_self");
			}
		}

		this.Table.dispatchEvent(new CustomEvent("rowpicked", { detail: { row: row } }));
	}

	SelectNextRow(isRange, solitary) { this.SelectRow(this.RowAt(this.LastSelectedIndex + 1), solitary, isRange); }
	SelectPreviousRow(isRange, solitary) { this.SelectRow(this.RowAt(Math.max(this.LastSelectedIndex - 1, 0)), solitary, isRange); }

	SelectRow(row, solitary, isRange) {
		if (row) {
			solitary = this.ForbidMultiSelect || solitary;
			isRange = !(this.ForbidMultiSelect || !isRange);

			if (!isRange) {
				let toggledCount = 0;

				if (solitary) {
					[...this.Table.querySelectorAll(`${this.DataRowSelector}.active`)].filter(x => x !== row).forEach(x => {
						x.removeClass("active");
						++toggledCount;
					});
				}

				// Toggle row selected state
				if (row.classList.contains("active") && toggledCount === 0) {
					row.removeClass("active");
				} else {
					row.classList.add("active");
					this.Table.dispatchEvent(new CustomEvent("rowselected", { detail: { row: row } }));
				}
			} else {
				if (solitary) {
					this.GetVisibleRows().forEach(x => x.classList.remove("active"));
				}

				const currentIndex = this.GetRowIndex(row),
					start = Math.min(currentIndex, this.LastSelectedIndex),
					end = Math.max(currentIndex, this.LastSelectedIndex);

				this.GetRowsFromIndices(Array(end - start + 1).fill().map((_, ix) => start + ix)).forEach(row => row.classList.add("active"));
			}

			const newIndex = this.GetRowIndex(row);

			if (newIndex !== -1) {
				this.LastSelectedIndex = newIndex;
			}
		}

		this.UpdateSelectedCount();
	}

	UpdateSelectedCount() {
		if (this.SelectedCountLabel) {
			const selectedCount = this.GetSelectedRows().length;

			this.SelectedCountLabel.textContent = selectedCount > 0
				? `(${selectedCount} selected)`
				: "";
		}
	}

	Search(query) {
		const arrRows = [...this.Table.querySelectorAll(this.ValidRowSelector)];

		if (arrRows.length) {
			const newSet = arrRows.search(query, "textContent", -1);

			arrRows.forEach(row => {
				row.toggleClass("d-none", !newSet.includes(row) && query.trim() !== "");

				if (row.classList.contains("d-none")) {
					row.removeClass("active");
				}
			});

			this.Table.querySelectorAll("tbody").forEach(body => {
				if (!body.querySelector("tr:not(.d-none,.table-body-label)")) {
					body.classList.add("d-none");
				} else {
					body.removeClasses(["collapsed", "d-none"]);
				}
			});
		}

		this.UpdateSelectedCount();
	}

	GetSelectedIndex() { return Array.from(this.Table.querySelectorAll(this.DataRowSelector)).indexOf(this.Table.querySelector(`${this.DataRowSelector}.active`)) };

	GetRowFromEvent(event, includeGroupRows) {
		if (event.target.matches(includeGroupRows ? this.RowSelector : this.DataRowSelector)) {
			return event.target;
		} else {
			return event.target.closest(includeGroupRows ? this.RowSelector : this.DataRowSelector);
		}
	}

	GetSelectedRows() { return Array.from(this.Table.querySelectorAll(`${this.DataRowSelector}.active`)); }
	RowAt(index) { return this.Table.querySelectorAll(this.DataRowSelector)[index]; }
	GetRowIndex(row) { return Array.from(this.Table.querySelectorAll(this.DataRowSelector)).indexOf(row); }

	GetRowsFromIndices(indices) { return Array.from(this.Table.querySelectorAll(this.DataRowSelector)).filter((_, ix) => indices.includes(ix)); }
	GetVisibleRows() { return Array.from(this.Table.querySelectorAll(this.DataRowSelector)); }
	GetAllRows() { return Array.from(this.Table.querySelectorAll(this.ValidRowSelector)); }

	GoToPage(pageNumber) {
		let page = parseInt(pageNumber);

		if (!isNaN(page)) {
			Browser.SetURLParameter("p", !page || page === 1 ? null : page, true);
		}
	}

	// Get's an object representing a rows data using the actual column names
	GetRowDetails(row) {
		const retVal = {};

		this.Table.querySelectorAll(this.HeaderSelector)
			.forEach((header, index) => retVal[header.textContent.trim().replace(" ", "_")] = row.querySelector(`td:nth-of-type(${index + 1})`).textContent.trim());

		return retVal;
	}

	static RefreshTables() {
		document.querySelectorAll(".chipside-table:not(.no-js)").forEach(x => x.Table ??= new Table(x));
	}
}